'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  Maximize2,
  Minimize2,
  Printer,
  RotateCw,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kit/ui/tooltip';

import {
  useReactPdf,
  type PdfDocumentLoadSuccess,
} from '../hooks/use-react-pdf';

interface PdfViewerProps {
  url: string;
  fileName: string;
  /** Initial scale (default: 1.0) */
  initialScale?: number;
  /** Whether to render text layer for selection (default: true) */
  renderTextLayer?: boolean;
  /** Whether to render annotation layer (default: true) */
  renderAnnotationLayer?: boolean;
  /** Callback when PDF fails to load */
  onError?: (error: Error) => void;
  /** Callback when PDF loads successfully */
  onLoadSuccess?: (data: PdfDocumentLoadSuccess) => void;
  /** Show print button (default: true) */
  showPrint?: boolean;
  /** Show download button (default: true) */
  showDownload?: boolean;
  /** Show fullscreen button (default: true) */
  showFullscreen?: boolean;
}

/**
 * PDF viewer component using react-pdf
 *
 * Features:
 * - SSR-safe dynamic loading
 * - Page navigation for multi-page PDFs
 * - Zoom controls with keyboard shortcuts
 * - Text selection support
 * - Print and download support
 * - Fullscreen mode
 * - Page rotation
 * - Go to page input
 * - Keyboard navigation (arrows, +/-, Home/End)
 */
/**
 * Passed to every PDF we render.
 *
 * `enableScripting: false` is belt-and-braces, NOT the thing that protects us,
 * and an earlier version of this comment claimed otherwise. Verified against
 * pdfjs-dist 5.4.296: react-pdf does spread `options` into
 * `pdfjs.getDocument()` (Document.js:215), but `getDocument` never reads
 * `enableScripting` — it reads `isEvalSupported`, `enableXfa` and friends, and
 * the 13 occurrences of `enableScripting` in the build all sit in the
 * AnnotationLayer. The key is also absent from react-pdf's dist entirely, and
 * pdfjs coerces it to false there anyway. So PDF JavaScript was never running
 * on this path, and setting this changes nothing today.
 *
 * What actually closed GHSA-hq66-cqwq-w95j was removing the unused
 * `pdfjs-dist ^5.6.205` devDependency: the advisory range is >=5.6.83 <6.2.108
 * and react-pdf pins 5.4.296, which is below it. Nothing imports `pdfjs-dist`
 * directly — only `reactPdf.pdfjs` via use-react-pdf.ts.
 *
 * Kept because it costs nothing (`getDocument` ignores unknown keys) and takes
 * effect if pdfjs ever wires it into the core API. Do not read it as the
 * reason uploaded PDFs are safe.
 *
 * Module scope on purpose: react-pdf compares `options` by identity and
 * re-fetches the whole document when a new object is passed each render.
 */
const PDF_DOCUMENT_OPTIONS = { enableScripting: false } as const;

export function PdfViewer({
  url,
  fileName,
  initialScale = 1.0,
  renderTextLayer = true,
  renderAnnotationLayer = true,
  onError,
  onLoadSuccess,
  showPrint = true,
  showDownload = true,
  showFullscreen = true,
}: PdfViewerProps) {
  const { t } = useTranslation();
  const {
    PdfComponents,
    isLoading: isPdfLoading,
    error: pdfError,
  } = useReactPdf();
  const containerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(initialScale);
  const [rotation, setRotation] = useState(0);
  const [isDocLoading, setIsDocLoading] = useState(true);
  const [docError, setDocError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pageInputValue, setPageInputValue] = useState('1');

  const handleLoadSuccess = useCallback(
    (data: PdfDocumentLoadSuccess) => {
      setNumPages(data.numPages);
      setIsDocLoading(false);
      onLoadSuccess?.(data);
    },
    [onLoadSuccess],
  );

  const handleLoadError = useCallback(
    (error: Error) => {
      setDocError(error.message);
      setIsDocLoading(false);
      onError?.(error);
    },
    [onError],
  );

  const handlePreviousPage = useCallback(() => {
    setPageNumber((prev) => {
      const newPage = Math.max(prev - 1, 1);
      setPageInputValue(String(newPage));
      return newPage;
    });
  }, []);

  const handleNextPage = useCallback(() => {
    if (numPages) {
      setPageNumber((prev) => {
        const newPage = Math.min(prev + 1, numPages);
        setPageInputValue(String(newPage));
        return newPage;
      });
    }
  }, [numPages]);

  const handleFirstPage = useCallback(() => {
    setPageNumber(1);
    setPageInputValue('1');
  }, []);

  const handleLastPage = useCallback(() => {
    if (numPages) {
      setPageNumber(numPages);
      setPageInputValue(String(numPages));
    }
  }, [numPages]);

  const handleGoToPage = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const page = parseInt(pageInputValue, 10);
      if (!isNaN(page) && page >= 1 && numPages && page <= numPages) {
        setPageNumber(page);
      } else {
        setPageInputValue(String(pageNumber));
      }
    },
    [pageInputValue, numPages, pageNumber],
  );

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  const handleResetZoom = useCallback(() => {
    setScale(1.0);
  }, []);

  const handleRotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const handlePrint = useCallback(() => {
    const printWindow = window.open(url);
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  }, [url]);

  const handleDownload = useCallback(async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, [url, fileName]);

  const handleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        await containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
    }
  }, [isFullscreen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in input
      if (e.target instanceof HTMLInputElement) return;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          handlePreviousPage();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          handleNextPage();
          break;
        case 'Home':
          e.preventDefault();
          handleFirstPage();
          break;
        case 'End':
          e.preventDefault();
          handleLastPage();
          break;
        case '+':
        case '=':
          e.preventDefault();
          handleZoomIn();
          break;
        case '-':
        case '_':
          e.preventDefault();
          handleZoomOut();
          break;
        case '0':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleResetZoom();
          }
          break;
        case 'r':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleRotate();
          }
          break;
        case 'f':
          if (showFullscreen) {
            e.preventDefault();
            handleFullscreen();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    handlePreviousPage,
    handleNextPage,
    handleFirstPage,
    handleLastPage,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    handleRotate,
    handleFullscreen,
    showFullscreen,
  ]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Loading react-pdf library
  if (isPdfLoading || !PdfComponents) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <Loader2 className="text-muted-foreground h-12 w-12 animate-spin" />
        <p className="text-muted-foreground text-sm">
          {t('documents:pdf.loadingViewer')}
        </p>
      </div>
    );
  }

  // Error loading react-pdf library
  if (pdfError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
        <FileText className="text-muted-foreground h-12 w-12" />
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">
            {t('documents:pdf.viewerUnavailableTitle')}
          </h3>
          <p className="text-muted-foreground text-sm">
            {t('documents:pdf.viewerUnavailableMessage')}
          </p>
        </div>
      </div>
    );
  }

  // Error loading document
  if (docError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
        <FileText className="text-muted-foreground h-12 w-12" />
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">
            {t('documents:pdf.loadFailedTitle')}
          </h3>
          <p className="text-muted-foreground text-sm">{docError}</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div ref={containerRef} className="flex h-full flex-col">
        {/* Controls */}
        <div className="bg-muted/30 flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
          {/* Left: Zoom Controls */}
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleZoomOut}
                  disabled={scale <= 0.5}
                  aria-label={t('documents:pdf.zoomOut')}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('documents:pdf.zoomOutTooltip')}
              </TooltipContent>
            </Tooltip>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="hover:bg-muted h-8 min-w-[50px] rounded px-2 py-1 text-center text-sm font-medium"
              onClick={handleResetZoom}
              aria-label={t('documents:pdf.resetZoom')}
            >
              {Math.round(scale * 100)}%
            </Button>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleZoomIn}
                  disabled={scale >= 3.0}
                  aria-label={t('documents:pdf.zoomIn')}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('documents:pdf.zoomInTooltip')}
              </TooltipContent>
            </Tooltip>

            <div className="bg-border mx-2 h-6 w-px" />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleRotate}
                  aria-label={t('documents:pdf.rotate')}
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('documents:pdf.rotateTooltip')}
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Center: Page Navigation */}
          {numPages && numPages > 1 && (
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handlePreviousPage}
                    disabled={pageNumber <= 1}
                    aria-label={t('documents:pdf.previousPage')}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('documents:pdf.previousPageTooltip')}
                </TooltipContent>
              </Tooltip>

              <form
                onSubmit={handleGoToPage}
                className="flex items-center gap-1"
              >
                <Input
                  type="text"
                  value={pageInputValue}
                  onChange={(e) => setPageInputValue(e.target.value)}
                  className="h-7 w-12 px-2 text-center text-sm"
                  aria-label={t('documents:pdf.goToPage')}
                />
                <span className="text-muted-foreground text-sm">
                  / {numPages}
                </span>
              </form>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleNextPage}
                    disabled={pageNumber >= numPages}
                    aria-label={t('documents:pdf.nextPage')}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('documents:pdf.nextPageTooltip')}
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          {/* Right: Actions */}
          <div className="flex items-center gap-1">
            {showPrint && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handlePrint}
                    aria-label={t('documents:pdf.print')}
                  >
                    <Printer className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('documents:pdf.print')}</TooltipContent>
              </Tooltip>
            )}

            {showDownload && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleDownload}
                    aria-label={t('documents:pdf.download')}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('documents:pdf.download')}</TooltipContent>
              </Tooltip>
            )}

            {showFullscreen && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleFullscreen}
                    aria-label={
                      isFullscreen
                        ? t('documents:pdf.exitFullscreen')
                        : t('documents:pdf.fullscreen')
                    }
                  >
                    {isFullscreen ? (
                      <Minimize2 className="h-4 w-4" />
                    ) : (
                      <Maximize2 className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isFullscreen
                    ? t('documents:pdf.exitFullscreenTooltip')
                    : t('documents:pdf.fullscreenTooltip')}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {/* PDF Content */}
        <div className="bg-muted/10 relative flex flex-1 items-center justify-center overflow-auto p-4">
          {isDocLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="text-muted-foreground h-12 w-12 animate-spin" />
            </div>
          )}

          <PdfComponents.Document
            file={url}
            options={PDF_DOCUMENT_OPTIONS}
            onLoadSuccess={handleLoadSuccess}
            onLoadError={handleLoadError}
            loading={null}
            className="flex justify-center"
          >
            <PdfComponents.Page
              pageNumber={pageNumber}
              scale={scale}
              rotate={rotation}
              renderTextLayer={renderTextLayer}
              renderAnnotationLayer={renderAnnotationLayer}
            />
          </PdfComponents.Document>
        </div>

        {/* Footer */}
        <div className="bg-muted/30 text-muted-foreground border-t px-4 py-2 text-xs">
          <p className="flex items-center gap-2">
            <FileText className="h-3 w-3" />
            {fileName}
            {numPages &&
              ` • ${t('documents:pdf.pageCount', { count: numPages })}`}
          </p>
        </div>
      </div>
    </TooltipProvider>
  );
}
